import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { CardDivider } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { TextLink } from '@/components/atoms/TextLink';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import { useCage } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { isValidCageId } from '@/domain/cages';
import { speciesTraits } from '@/domain/cages/speciesTraits';
import type { HealthParameterKey } from '@/domain/health';
import { useGetHealthOverview } from '@/domain/health';
import { localizedDate } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import type {
    HealthDetailPageId,
    HealthDetailRouteLabelKey,
} from '../../_config/healthDetailPageConfig';
import { getHealthProtocolHref } from '../../_config/healthProtocolLinks';
import { findHealthOverviewParameter } from '../../_utils/healthDetailOverviewHelpers';
import { getHealthScoreDescriptionKey } from '../../_utils/healthLocationFishType';
import { getParameterFishCountOnLastMeasurementDay } from '../../HealthDashboard/healthOverviewChartMappers';
import HealthParameterDevelopmentMetric from '../HealthParameterDevelopmentMetric/HealthParameterDevelopmentMetric';
import HealthParameterStatusMetric from '../HealthParameterStatusMetric/HealthParameterStatusMetric';

import styles from './HealthDetailInformationPanel.module.scss';

const SCORE_KEYS = ['score1', 'score2', 'score3'] as const;

type ScoreTranslationKey = (typeof SCORE_KEYS)[number];

const SCORE_LABEL_KEYS: Record<ScoreTranslationKey, string> = {
    score1: 'dashboard.laksvelScore1',
    score2: 'dashboard.laksvelScore2',
    score3: 'dashboard.laksvelScore3',
};

export interface HealthDetailInformationPanelProps {
    healthParameterKey: HealthParameterKey;
    detailNamespace: HealthDetailPageId;
    routeLabelKey: HealthDetailRouteLabelKey;
}

export default function HealthDetailInformationPanel(props: HealthDetailInformationPanelProps) {
    const { healthParameterKey, detailNamespace, routeLabelKey } = props;
    const { t, i18n } = useTranslation('healthModule');
    const { cageId, stock } = useCage();
    // The maturation wording is species-specific, so it follows the PEN, which is what
    // `stock.fishType` carries now (WB-2207). Which wording a species uses is a trait rather
    // than a comparison here, so a species added later gets the default set until someone
    // writes one for it.
    const maturationCopy = speciesTraits(stock?.fishType?.shortName).maturationCopy;
    const isRainbowTrout = maturationCopy === 'rainbowTrout';
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const locale = parseLocale(i18n.language);

    const overviewQueryParams = useMemo(
        () => ({ daypartTag, fishGroupId: selectedFishGroupId }),
        [daypartTag, selectedFishGroupId]
    );

    const {
        data: overview,
        isLoading,
        isFetching,
    } = useGetHealthOverview(cageId, overviewQueryParams, {
        enabled: isValidCageId(cageId),
        staleTime: 5 * 60 * 1000,
    });

    const loading = isLoading || isFetching;

    const parameter = useMemo(
        () => findHealthOverviewParameter(overview, healthParameterKey),
        [overview, healthParameterKey]
    );

    const fishCount =
        parameter != null ? getParameterFishCountOnLastMeasurementDay(parameter) : null;
    const fishCountLabel =
        fishCount != null
            ? formatNumberByLocale(fishCount, locale, 0)
            : t('dashboard.notAvailable');

    const lastMeasurement = overview?.context.asOf ?? '';
    const protocolHref = getHealthProtocolHref(detailNamespace, i18n.language);

    return (
        <ContentBlock
            title={t('detail.common.informationTitle')}
            titleIcon={<TitleIcon icon={Info} />}
            className={styles.infoPanel}
            spacing="none">
            <div className={styles.panelBody} data-testid="health-detail-information-panel">
                <div className={styles.metricBlock}>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.statusTitle')}
                        </Overline>
                        {loading ? (
                            <SkeletonBox
                                width={80}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : parameter != null ? (
                            <div className={styles.metricValueRow}>
                                <HealthParameterStatusMetric parameter={parameter} />
                            </div>
                        ) : (
                            <p className={styles.metricValue}>{t('dashboard.notAvailable')}</p>
                        )}
                    </div>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.developmentTitle')}
                        </Overline>
                        {loading ? (
                            <SkeletonBox
                                width={80}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : (
                            <div className={styles.metricValueRow}>
                                <HealthParameterDevelopmentMetric parameter={parameter} />
                            </div>
                        )}
                    </div>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.measuredFishCountTitle')}
                        </Overline>
                        {loading ? (
                            <SkeletonBox
                                width={64}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : (
                            <p className={styles.metricValue}>{fishCountLabel}</p>
                        )}
                    </div>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.lastMeasurement')}
                        </Overline>
                        {loading ? (
                            <SkeletonBox
                                width={100}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : (
                            <p className={styles.metricValue}>
                                {lastMeasurement
                                    ? localizedDate(lastMeasurement, locale)
                                    : t('dashboard.notAvailable')}
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.divider}>
                    <CardDivider />
                </div>

                {loading ? (
                    <SkeletonBox
                        width="100%"
                        height={120}
                        borderRadius={8}
                        className={styles.skeleton}
                    />
                ) : (
                    <>
                        <div className={styles.scoreSection}>
                            <p className={styles.scoreIntro}>
                                {t('dashboard.laksvelDescriptionBody')}
                            </p>
                            <ul className={styles.scoreList}>
                                {SCORE_KEYS.map((scoreKey: ScoreTranslationKey) => (
                                    <li key={scoreKey} className={styles.scoreItem}>
                                        <p className={styles.scoreLabel}>
                                            {t(SCORE_LABEL_KEYS[scoreKey])}
                                        </p>
                                        <p className={styles.scoreDescription}>
                                            {t(
                                                getHealthScoreDescriptionKey(
                                                    detailNamespace,
                                                    scoreKey,
                                                    isRainbowTrout
                                                )
                                            )}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {detailNamespace === 'maturation' && !isRainbowTrout && (
                            <div className={styles.downgradeNote}>
                                <Alert tone="warning">
                                    {t('detail.maturation.downgradeEstimateNote')}
                                </Alert>
                            </div>
                        )}

                        {protocolHref.startsWith('http') ? (
                            <TextLink
                                tone="underlined"
                                className={styles.protocolLink}
                                href={protocolHref}
                                target="_blank"
                                rel="noopener noreferrer">
                                {t('detail.common.protocolLinkLabel', {
                                    parameter: t(`routes.${routeLabelKey}`),
                                })}
                            </TextLink>
                        ) : null}
                    </>
                )}
            </div>
        </ContentBlock>
    );
}
